/**
 * @voltxampere/web — Transient runner store (Sprint 15)
 *
 * Mirrors the Sprint 14 `dc-result` shape but for transient
 * analyses: signal-driven `currentTransientResult` + `running` +
 * `error` + `live` (continuous mode flag). The runner is injected
 * from `main.ts` so tests can supply a fake without spawning an
 * engine worker.
 *
 * Live mode is implemented as a polling loop that re-issues
 * `runTransient` whenever the previous response settles. The
 * incoming time series is shifted into the per-channel sample
 * buffers via `pushTransientSamples`.
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { signal } from "@preact/signals-core";
import type { Circuit } from "@voltxampere/schema";
import type { ChannelId } from "@voltxampere/ui/waveform";

import { channels } from "./channels.js";
import { playbackSpeedSignal } from "./playback-speed.js";
import { probeBindings } from "./probes.js";
import { gateCircuitForEngine } from "./validation-gate.js";

/**
 * Wire shape the engine returns. Only the fields the UI actually
 * needs are typed; consumers should treat `meta` as opaque.
 */
export interface TransientWireSample {
  readonly t: number;
  readonly nodeVoltages: Record<string, number>;
  readonly sourceCurrents: Record<string, number>;
}

export interface TransientResultLike {
  readonly converged: boolean;
  readonly samples: number;
  readonly timeSeries: readonly TransientWireSample[];
  readonly warnings: readonly string[];
}

export type TransientRunner = (
  circuit: Circuit,
  opts?: TransientRunOpts,
) => Promise<TransientResultLike>;

export interface TransientRunOpts {
  readonly tstop?: number;
  readonly signal?: AbortSignal;
}

let runner: TransientRunner | null = null;

export function setTransientRunner(r: TransientRunner | null): void {
  runner = r;
}

export const currentTransientResult = signal<TransientResultLike | null>(null);
export const transientRunning = signal<boolean>(false);
export const transientError = signal<string | null>(null);
export const transientLive = signal<boolean>(false);

let liveLoopAbort: AbortController | null = null;

// Sprint 25 R3 lane 3 — single-shot concurrent-run guard. Live
// mode keeps using `liveLoopAbort` for its outer driver; this
// extra controller protects single-shot F5 presses against
// double-firing while the previous solve is still in flight.
let activeSingleShotAbort: AbortController | null = null;

/**
 * Single-shot transient run. Pushes the resulting time series into
 * the per-channel sample buffers via `pushTransientSamples`.
 * Surfaces `running` + `error` for the inspector / waveform UI.
 */
export async function runTransient(circuit: Circuit, opts: TransientRunOpts = {}): Promise<void> {
  if (!runner) {
    transientError.value = "Transient runner not configured";
    if (import.meta.env.DEV) {
      console.warn("[VXA F5] runTransient called but no runner configured (worker boot failed?)");
    }
    return;
  }
  // Sprint 25 R2 lane 2 — pre-engine validation gate (TS-008). Live
  // mode re-enters this function every iteration; toast queue's
  // MAX_TOASTS FIFO + Infinity-duration on errors throttles spam.
  if (!gateCircuitForEngine(circuit, { warnings: true })) {
    return;
  }
  // Sprint 25 R3 lane 3 — abort any prior single-shot solve. Live
  // mode handles its own abortion via `liveLoopAbort` and uses
  // `opts.signal`; this guard only fires when the caller is
  // re-entering single-shot (F5 again before the previous F5
  // settled).
  if (opts.signal === undefined && activeSingleShotAbort !== null) {
    activeSingleShotAbort.abort();
  }
  const ctl = opts.signal === undefined ? new AbortController() : null;
  if (ctl !== null) activeSingleShotAbort = ctl;
  transientRunning.value = true;
  transientError.value = null;
  if (import.meta.env.DEV) {
    console.info(
      `[VXA F5] runTransient start · circuit=${circuit.name} components=${String(circuit.components.length)}`,
    );
  }
  // Sprint 53 — playback speed multiplier. At speed=1 (default) opts
  // pass through unchanged so all pre-S53 callers keep their exact
  // behaviour. At other speeds the effective tstop is base × speed,
  // where base is the caller's opts.tstop OR the circuit's
  // simulationSettings.transient.tstop OR the SPICE default 1e-3. The
  // engine never sees the speed concept — it just receives a pre-
  // scaled tstop. Engine zero-diff invariant preserved.
  const speed = playbackSpeedSignal.value;
  const effectiveOpts: TransientRunOpts =
    speed === 1
      ? opts
      : {
          ...opts,
          tstop: (opts.tstop ?? circuit.simulationSettings.transient.tstop) * speed,
        };
  try {
    const result = await runner(circuit, effectiveOpts);
    if (ctl?.signal.aborted === true) return;
    currentTransientResult.value = result;
    if (import.meta.env.DEV) {
      console.info(
        `[VXA F5] runTransient result · converged=${String(result.converged)} samples=${String(result.samples)} timeSeries=${String(result.timeSeries.length)}`,
      );
    }
    pushTransientSamples(result);
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") {
      // Cancelled by stopLive — not an error.
      return;
    }
    if (ctl?.signal.aborted === true) return;
    transientError.value = err instanceof Error ? err.message : String(err);
    if (import.meta.env.DEV) {
      console.error("[VXA F5] runTransient threw:", err);
    }
  } finally {
    if (ctl !== null && activeSingleShotAbort === ctl) {
      activeSingleShotAbort = null;
      transientRunning.value = false;
    } else if (ctl === null) {
      transientRunning.value = false;
    }
  }
}

/**
 * Start continuous transient simulation. `circuitGetter` returns
 * the current circuit — the loop re-reads it each iteration so
 * inspector edits flow into the next sweep. Stops when
 * `stopLive()` runs or any solve rejects with a non-abort error.
 */
export function startLive(circuitGetter: () => Circuit | null, opts: TransientRunOpts = {}): void {
  if (transientLive.value) return;
  transientLive.value = true;
  liveLoopAbort = new AbortController();
  const ac = liveLoopAbort;
  void (async () => {
    while (transientLive.value && !ac.signal.aborted) {
      const circuit = circuitGetter();
      if (!circuit) break;
      await runTransient(circuit, { ...opts, signal: ac.signal });
      // Defer to the event loop so single-frame consumers can react
      // before we kick off the next iteration. A naïve `while`
      // without yielding would starve the rAF scheduler.
      await new Promise((resolve) => setTimeout(resolve, 16));
    }
  })();
}

export function stopLive(): void {
  transientLive.value = false;
  liveLoopAbort?.abort();
  liveLoopAbort = null;
}

/**
 * Project the transient response onto each enabled probe-bound
 * channel's sample buffer. Math channels rebuild from their
 * expression in `vxa-waveform` — they do not push raw samples
 * here.
 */
export function pushTransientSamples(result: TransientResultLike): void {
  const bindings = probeBindings.value;
  const channelMap = channels.value;
  let pushedCount = 0;
  let droppedNets = 0;
  for (const sample of result.timeSeries) {
    for (const [netId, voltage] of Object.entries(sample.nodeVoltages)) {
      const channelId: ChannelId | undefined = bindings.get(netId);
      if (!channelId) {
        droppedNets++;
        continue;
      }
      const state = channelMap.get(channelId);
      if (!state?.enabled) continue;
      state.buffer.push(sample.t, voltage);
      pushedCount++;
    }
  }
  if (import.meta.env.DEV) {
    const bindingMap = Array.from(bindings.entries())
      .map(([net, ch]) => `${ch}→${net}`)
      .join(",");
    console.info(
      `[VXA F5] pushTransientSamples · bindings=${bindingMap || "(none)"} pushed=${String(pushedCount)} dropped=${String(droppedNets)} timeSteps=${String(result.timeSeries.length)}`,
    );
  }
}

/** Test cleanup — wipes the result + state signals. */
export function resetTransientStore(): void {
  currentTransientResult.value = null;
  transientRunning.value = false;
  transientError.value = null;
  transientLive.value = false;
  liveLoopAbort?.abort();
  liveLoopAbort = null;
}
